import { Component, inject } from "@angular/core";
import { FormArray, FormBuilder, ReactiveFormsModule, Validators } from "@angular/forms";
import { Router } from "@angular/router";
import { AppButtonComponent } from "../../../../Common/Components/Button/ButtonComponent";
import { AppCardComponent } from "../../../../Common/Components/Card/CardComponent";
import { ToastEffects } from "../../../../Common/Components/Toast/State/ToastEffects";
import { NgGlyph } from "@ng-icons/core";
import { PrintingEffects } from "../../State/PrintingEffects";
import { PrintingUpsertItem } from "../../Dto/CreatePrintingRequest";
import { AutocompleteComponent } from "../../../../Common/Components/Autocomplete/AutocompleteComponent";
import { AutocompleteOption } from "../../../../Common/Components/Autocomplete/Type/AutocompleteTypes";
import { RichTextEditorComponent } from "../../../../Common/Components/RichTextEditor/RichTextEditorComponent";
import { VendorApi } from "../../../Vendor/Api/VendorApi";
import { VendorType } from "../../../Vendor/Dto/VendorModel";
import { WarehouseApi } from "../../../Warehouse/Api/WarehouseApi";
import { MaterialApi } from "../../../Material/Api/MaterialApi";
import { getBackendErrorMessage } from "../../../../Common/Utility/UtilityError";
import { QtyInputSelectZeroDirective } from "../../../../Common/Components/QtyInput/QtyInputSelectZeroDirective";
import { MaterialAutocompleteOption, toMaterialAutocompleteOption } from "../../../../Common/Utility/UtilityMaterialSequence";

@Component({
  selector: "app-printing-create",
  standalone: true,
  imports: [
    ReactiveFormsModule,
    AppButtonComponent,
    AppCardComponent,
    AutocompleteComponent,
    RichTextEditorComponent,
    QtyInputSelectZeroDirective,
    NgGlyph,
  ],
  templateUrl: "./PrintingCreatePage.html",
  styleUrl: "./PrintingCreatePage.scss",
})
export class PrintingCreatePage {
  private readonly fb = inject(FormBuilder);
  private readonly printingEffects = inject(PrintingEffects);
  private readonly toastEffects = inject(ToastEffects);
  private readonly router = inject(Router);
  private readonly vendorApi = inject(VendorApi);
  private readonly warehouseApi = inject(WarehouseApi);
  private readonly materialApi = inject(MaterialApi);

  isSaving = false;
  vendorOptions: AutocompleteOption[] = [];
  warehouseOptions: AutocompleteOption[] = [];
  materialOptions: MaterialAutocompleteOption[] = [];

  form = this.fb.group({
    vendorId: ["", [Validators.required]],
    warehouseId: ["", [Validators.required]],
    note: [""],
    items: this.fb.array([this.createItemForm()]),
  });

  get items(): FormArray {
    return this.form.get("items") as FormArray;
  }

  private createItemForm() {
    return this.fb.group({
      materialId: ["", [Validators.required]],
      plannedQty: [1, [Validators.required, Validators.min(1)]],
    });
  }

  addItem(): void {
    this.items.push(this.createItemForm());
  }

  removeItem(index: number): void {
    if (this.items.length <= 1) return;
    this.items.removeAt(index);
  }

  async save(): Promise<void> {
    if (this.isSaving || this.form.invalid) {
      return;
    }

    this.isSaving = true;
    try {
      const created = await this.printingEffects.createPrinting({
        vendorId: (this.form.value.vendorId ?? "").trim(),
        warehouseId: (this.form.value.warehouseId ?? "").trim(),
        note: (this.form.value.note ?? "").trim(),
        items: (this.items.value as PrintingUpsertItem[]).map((item) => ({
          materialId: item.materialId,
          plannedQty: Number(item.plannedQty),
        })),
      });
      this.toastEffects.show("Printing created.", "success");
      await this.router.navigate(["/printings/edit", created.id]);
    } catch (exception: unknown) {
      this.toastEffects.show(getBackendErrorMessage(exception, "Unable to create printing."), "error");
    } finally {
      this.isSaving = false;
    }
  }

  goBack(): void {
    this.router.navigate(["/printings"]).catch(() => undefined);
  }

  async searchVendors(term: string): Promise<void> {
    const response = await this.vendorApi.searchVendors({ search: term, type: VendorType.Printing, page: 1, limit: 50 });
    this.vendorOptions = response.data.map((item) => ({ value: item.id, label: item.name }));
  }

  async searchWarehouses(term: string): Promise<void> {
    const response = await this.warehouseApi.searchWarehouses({ search: term, page: 1, limit: 50 });
    this.warehouseOptions = response.data.map((item) => ({ value: item.id, label: item.name }));
  }

  async searchMaterials(term: string): Promise<void> {
    const response = await this.materialApi.searchMaterials({ search: term, page: 1, limit: 50 });
    this.materialOptions = response.data.map((item) => toMaterialAutocompleteOption(item));
  }

  onVendorChange(value: string | number | (string | number)[]): void {
    if (Array.isArray(value)) return;
    this.form.patchValue({ vendorId: value.toString() });
  }

  onWarehouseChange(value: string | number | (string | number)[]): void {
    if (Array.isArray(value)) return;
    this.form.patchValue({ warehouseId: value.toString() });
  }

  onMaterialChange(index: number, value: string | number | (string | number)[]): void {
    if (Array.isArray(value)) return;
    this.items.at(index).patchValue({ materialId: value.toString() });
  }
}
